import type { ReactNode } from 'react';
import { type VariantProps, tv } from 'tailwind-variants';

import type { CommonProps } from '../../types';
import {
    Button,
    Container,
    HeadingLayout,
    RichText,
    Swimlane,
} from '@beat-venues/ui/next';
import type { JSX } from 'react';
import Image from 'next/image';
import type { Document } from '@contentful/rich-text-types';
import { AnalyticsButtonInfo } from '@beat-venues/common-types';
import { useTranslation } from 'react-i18next';

export interface SignatureBannerProps
    extends CommonProps,
        VariantProps<typeof SignatureBannerStyles> {
    seriesName: string;
    tourName: string;
    content: Document;
    locale: string;
    onSignatureClick: (value: AnalyticsButtonInfo) => void;
    children?: ReactNode;
}

export const SignatureBannerStyles = tv({
    slots: {
        root: '',
    },
    variants: {},
});

const SIGNATURE_LANDING_URLS: Record<string, string | undefined> = {
    NL: process.env.NEXT_PUBLIC_SIGNATURE_LADING_URL_NL,
    FR: process.env.NEXT_PUBLIC_SIGNATURE_LADING_URL_FR,
    EN: process.env.NEXT_PUBLIC_SIGNATURE_LADING_URL_EN,
};

const withQueryParams = (
    url: string | undefined,
    params: Record<string, string>
): string | undefined => {
    if (!url) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${new URLSearchParams(params).toString()}`;
};

export const SignatureBanner = ({
    seriesName,
    tourName,
    className,
    children,
    content,
    locale,
    onSignatureClick,
    testId = 'signature',
    ...props
}: SignatureBannerProps): JSX.Element => {
    const { root } = SignatureBannerStyles();
    const { t } = useTranslation('event');

    const landingUrl = withQueryParams(
        SIGNATURE_LANDING_URLS[locale.toUpperCase()],
        {
            ...(seriesName && { seriesName: seriesName }),
            ...(tourName && { tourName: tourName }),
        }
    );

    return (
        <div className={root({ className })} {...props} data-testid={testId}>
            <Swimlane>
                <div className="h-[15rem] w-full bg-[url('/assets/vip/signature/afas-lounge.jpg')] bg-cover bg-[position:50%_130%] lg:h-[18rem] lg:bg-[position:50%_56%]" />
                <div className="relative -mt-[176px]">
                    <div className="absolute inset-0 bg-[url('/assets/vip/signature/signature.jpg')] bg-cover bg-[position:50%_60%] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_176px)] [mask-image:linear-gradient(to_bottom,transparent,black_176px)]" />
                    <HeadingLayout className="relative pb-16 pt-48 text-black">
                        <Container
                            maxWidth="xl"
                            className="flex flex-col items-start justify-around gap-8 lg:flex-row"
                        >
                            <Image
                                src="/assets/vip/signature/signature-icon.svg"
                                alt="Signature"
                                width={90}
                                height={90}
                            />
                            <RichText body={content} locale={locale} />
                            <section className="mt-8 flex min-w-[250px] flex-col gap-4 lg:m-auto">
                                <Button
                                    className="self-auto"
                                    intent="signature"
                                    href={`${landingUrl}#contact-form`}
                                    onClick={() => {
                                        const buttonInfo: AnalyticsButtonInfo =
                                            {
                                                event: 'button_click',
                                                id: `signature-${seriesName}`,
                                                name: 'signature',
                                                text: t(
                                                    `ctaStatusLabels.signature`
                                                ),
                                                category: 'signature link',
                                                type: 'cta',
                                                target_url: landingUrl || '',
                                            };
                                        if (onSignatureClick) {
                                            onSignatureClick(buttonInfo);
                                        }
                                    }}
                                >
                                    {t(`ctaStatusLabels.signature`)}
                                </Button>
                                <Button
                                    className="self-auto"
                                    href={landingUrl}
                                    intent="quaternary"
                                >
                                    {t('ctaLabels.moreInfo')}
                                </Button>
                            </section>
                        </Container>
                    </HeadingLayout>
                </div>
            </Swimlane>
        </div>
    );
};
